import { Box, Paper, Skeleton } from '@mui/material';
import { useSelector } from 'react-redux';
import { Navigate, Outlet } from 'react-router';
import { BreadCrumbCustom } from '../components/BreadCrumbCustom/BreadCrumbCustom';
import { useLang } from '../language';
import { RootState } from '../store';

/**
 * Guard del módulo de empresas (`/empreses/*`).
 *
 * Solo deja entrar a quien tiene al menos una empresa con acceso autorizado, que es el
 * mismo criterio que usa el menú para mostrar el enlace. El backend ya responde 403 por
 * empresa (`IntegraWeb\Empresa\EmpresaController::show`), así que esto es UX, no seguridad.
 *
 * Los tres estados del flag importan:
 *  - `null` (todavía no consultado): esqueleto y no se decide nada. Es el estado inicial,
 *    así que sin esta rama un F5 sobre `/empreses` redirigiría a home antes de que llegara
 *    la respuesta y nunca se podría entrar.
 *  - `false`: a home.
 *  - `true`: adelante.
 *
 * A home se va a `/el-meu-col-legi`, que es lo que resuelve el `path="*"` de `PrivateRouter`.
 *
 * El flujo de pedir acceso y dar de alta una empresa ya no vive aquí: está en el botón de
 * la cabecera (`components/Layouts/LayoutPrimary/Navtop.tsx`), visible siempre, para que
 * quien no tiene ninguna empresa pueda pedirla aunque esta ruta lo eche a home.
 */
export const AccesoEmpresasRoute = () => {

    const Lang = useLang()

    const tieneEmpresasAutorizadas = useSelector(
        (state: RootState) => state.empresa.tieneEmpresasAutorizadas
    )

    if (tieneEmpresasAutorizadas === null) {

        // Esqueleto con la forma real de la página (migas + tarjeta de búsqueda + tabla),
        // no un bloque suelto: así el salto al contenido, cuando llega la respuesta, no
        // mueve la página.
        return (
            <div>

                <BreadCrumbCustom items={[{ title: Lang('EMPRESAS'), link: '/empreses' }]} />

                <section className='LayoutPrimary__contentmain'>

                    <Paper sx={{ p: 3 }}>

                        <Skeleton variant='text' width={180} height={32} />

                        <Box
                            sx={{
                                display: 'flex',
                                flexDirection: { xs: 'column', sm: 'row' },
                                alignItems: { xs: 'stretch', sm: 'flex-end' },
                                gap: { xs: 1.5, sm: 1 },
                                justifyContent: 'flex-end',
                                mt: 2,
                                mb: 1,
                            }}
                        >
                            <Skeleton variant='rounded' height={40} sx={{ flex: '1 1 300px' }} />

                            <Box sx={{ display: 'flex', gap: 1 }}>
                                <Skeleton variant='rounded' width={120} height={40} />
                                <Skeleton variant='rounded' width={110} height={40} />
                            </Box>
                        </Box>

                    </Paper>

                    <Paper sx={{ p: 3, mt: 3 }}>

                        <Skeleton variant='text' width={220} height={28} />

                        {
                            Array.from({ length: 5 }).map((_, index) => (
                                <Skeleton
                                    key={`skeleton-${index}`}
                                    variant='rounded'
                                    height={44}
                                    sx={{ mt: index === 0 ? 2 : 1 }}
                                />
                            ))
                        }

                    </Paper>

                </section>

            </div>
        )
    }

    return tieneEmpresasAutorizadas
        ? <Outlet />
        : <Navigate to='/el-meu-col-legi' replace />
}
